'use client'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useRouter } from 'next/navigation'
import { useEffect, useState } from 'react'
import useAuth from '../../hooks/useAuth'
import { resolvePostLoginPath } from '../../lib/crossDomainAuth'
import { effectiveBrandAccess } from '../../lib/pageAccess'
import { sanitizeInternalRedirect } from '../../utils/safeRedirect'
import LoginModal from '../dashboard/LoginModal'
import { FlexCol } from '../layout'

export type PortalVariant = 'portal' | 'admin'

const COPY: Record<
  PortalVariant,
  {
    overline: string
    title: string
    description: string
    cta: string
    home: string
  }
> = {
  portal: {
    overline: 'Portal Access',
    title: 'Access the WyseHub partner portal.',
    description:
      'Sign in to review referrals, claims, dashboards, requests, and daily operational activity across the WyseHub platform.',
    cta: 'Open Portal',
    home: '/homehub',
  },
  admin: {
    overline: 'Admin Access',
    title: 'Access the WyseHub administration area.',
    description:
      'Sign in with a system administration account to manage users, partners, settings, workflows, and operational controls.',
    cta: 'Open Admin',
    home: '/admin',
  },
}

/**
 * The entry page on the WyseHub portal and admin hosts, moved out of the old homepage
 * component when the apex became a server-rendered marketing page. Behaviour is the
 * old page's: a signed-in user is sent on to the right place, `?login=true` opens the
 * modal (storing a sanitised `next` for it to follow), and the button opens the modal or
 * goes straight in. The variant comes from the proxy's rewrite, read on the server,
 * rather than from `window.location`, so this renders on the first pass.
 */
export function PortalEntryPage({
  variant,
}: {
  readonly variant: PortalVariant
}) {
  const router = useRouter()
  const { user } = useAuth()
  const [open, setOpen] = useState(false)
  const copy = COPY[variant]

  useEffect(() => {
    const currentSearchParams = new URLSearchParams(window.location.search)
    const shouldOpenLogin = currentSearchParams.get('login') === 'true'

    if (user) {
      if (variant === 'admin') {
        router.replace('/admin')
        return
      }
      router.replace(
        resolvePostLoginPath(
          effectiveBrandAccess(user),
          window.location.hostname,
        ),
      )
      return
    }

    if (shouldOpenLogin) {
      setOpen(true)
      const nextUrl = sanitizeInternalRedirect(
        currentSearchParams.get('next'),
        window.location.origin,
      )
      if (nextUrl) {
        localStorage.setItem('loginRedirectUrl', nextUrl)
      } else {
        localStorage.removeItem('loginRedirectUrl')
      }
    }
  }, [user, router, variant])

  const onSignIn = () => {
    if (user) {
      router.push(copy.home)
    } else {
      setOpen(true)
    }
  }

  return (
    <>
      <Box
        // biome-ignore lint/plugin: full-viewport hero band
        sx={{ minHeight: '100vh', maxWidth: 1280, mx: 'auto', px: 3 }}
      >
        <FlexCol
          gap="xl"
          align="center"
          justify="center"
          // biome-ignore lint/plugin: centred hero copy
          sx={{ textAlign: 'center', minHeight: '100vh' }}
        >
          <Box
            component="img"
            src="/assets/images/WH-logo.png"
            alt="WyseHub logo"
            // biome-ignore lint/plugin: logo media dimensions
            sx={{ width: 220, maxWidth: '70vw' }}
          />
          <FlexCol
            gap="md"
            // biome-ignore lint/plugin: hero copy column max-width
            sx={{ maxWidth: 720 }}
          >
            <Typography variant="overline">{copy.overline}</Typography>
            <Typography variant="display">{copy.title}</Typography>
            <Typography variant="body1" color="text.secondary">
              {copy.description}
            </Typography>
          </FlexCol>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Button variant="contained" onClick={onSignIn}>
              {user ? copy.cta : `Sign in to ${copy.cta}`}
            </Button>
            <Button variant="outlined" component="a" href="/privacy">
              Privacy
            </Button>
          </Stack>
        </FlexCol>
      </Box>
      <LoginModal open={open} onClose={() => setOpen(false)} />
    </>
  )
}
